import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { FamilyType } from "@/features/Login/Family/types.ts";
import { useAddHouse, useRemoveHouse } from "@/hooks/familyHooks";

interface HouseData {
  name: string;
  address: string;
}

interface EditHouseData extends HouseData {
  id?: string;
}

interface HouseManagerProps {
  family: FamilyType;
  isOwner: boolean;
}

const HouseManager: React.FC<HouseManagerProps> = ({ family, isOwner }) => {
  const [isAddingHouse, setIsAddingHouse] = useState(false);
  const [editingHouse, setEditingHouse] = useState<EditHouseData | null>(null);
  const [newHouse, setNewHouse] = useState<HouseData>({
    name: "",
    address: "",
  });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const addHouseMutation = useAddHouse();
  const removeHouseMutation = useRemoveHouse();

  const handleAddNewHouse = () => {
    setIsAddingHouse(true);
    setNewHouse({ name: "", address: "" });
  };

  const handleEditHouse = (house: {
    _id: string;
    name: string;
    address: string;
  }) => {
    setEditingHouse({
      id: house._id,
      name: house.name,
      address: house.address,
    });
  };

  const handleDeleteHouse = (id: string) => {
    setConfirmDeleteId(id);
  };

  const confirmDeleteHouse = async () => {
    if (!confirmDeleteId) return;

    try {
      await removeHouseMutation.mutateAsync(confirmDeleteId);
      setConfirmDeleteId(null);
    } catch (error) {
      console.error("Erreur lors de la suppression de la maison", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isNewHouse: boolean
  ) => {
    const { name, value } = e.target;

    if (isNewHouse) {
      setNewHouse((prev) => ({ ...prev, [name]: value }));
    } else if (editingHouse) {
      setEditingHouse((prev) => (prev ? { ...prev, [name]: value } : null));
    }
  };

  const handleSaveHouse = async (isNew: boolean) => {
    try {
      if (isNew) {
        await addHouseMutation.mutateAsync(newHouse);
        setIsAddingHouse(false);
        setNewHouse({ name: "", address: "" });
      } else if (editingHouse && editingHouse.id) {
        // Implémenter updateHouse mutation ici
        // await updateHouseMutation.mutateAsync({ id: editingHouse.id, ...editingHouse });
        setEditingHouse(null);
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la maison", error);
    }
  };

  const renderHouseForm = (house: HouseData, isNew: boolean) => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Nom de la maison"
          name="name"
          value={house.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleInputChange(e, isNew)
          }
          required
          variant="outlined"
          size="small"
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Adresse"
          name="address"
          value={house.address}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleInputChange(e, isNew)
          }
          variant="outlined"
          size="small"
          placeholder="Optionnel"
        />
      </Grid>
    </Grid>
  );

  return (
    <>
      {isOwner && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h2">
            Maisons de la famille
          </Typography>

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            color="primary"
            onClick={handleAddNewHouse}
            disabled={isAddingHouse}
          >
            Ajouter
          </Button>
        </Box>
      )}

      {isAddingHouse && (
        <Card sx={{ mb: 3, borderLeft: 4, borderColor: "primary.main" }}>
          <CardHeader title="Nouvelle maison" />
          <CardContent>{renderHouseForm(newHouse, true)}</CardContent>
          <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
            <Button
              startIcon={<CancelIcon />}
              onClick={() => setIsAddingHouse(false)}
              sx={{ mr: 1 }}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={() => handleSaveHouse(true)}
              disabled={!newHouse.name || addHouseMutation.isPending}
            >
              {addHouseMutation.isPending ? (
                <CircularProgress size={24} />
              ) : (
                "Enregistrer"
              )}
            </Button>
          </CardActions>
        </Card>
      )}

      {family.houses.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Aucune maison n'a encore été ajoutée à votre famille.
          {isOwner &&
            " Vous pouvez ajouter une maison avec le bouton ci-dessus."}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {family.houses.map((house) => (
            <Grid item xs={12} sm={6} md={4} key={house._id}>
              <Card
                elevation={2}
                sx={{
                  height: "100%",
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
              >
                {editingHouse && editingHouse.id === house._id ? (
                  <>
                    <CardContent>
                      {renderHouseForm(editingHouse, false)}
                    </CardContent>
                    <CardActions sx={{ justifyContent: "flex-end", p: 2 }}>
                      <Button
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={() => setEditingHouse(null)}
                        sx={{ mr: 1 }}
                      >
                        Annuler
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSaveHouse(false)}
                      >
                        Enregistrer
                      </Button>
                    </CardActions>
                  </>
                ) : (
                  <>
                    <CardHeader
                      avatar={<HomeIcon color="primary" />}
                      title={house.name}
                      titleTypographyProps={{ fontWeight: "bold" }}
                    />
                    <CardContent>
                      {house.address ? (
                        <Box display="flex" alignItems="top" mt={1}>
                          <LocationIcon
                            fontSize="small"
                            sx={{ mr: 1, mt: 0.3, color: "text.secondary" }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {house.address}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontStyle="italic"
                        >
                          Aucune adresse spécifiée
                        </Typography>
                      )}
                    </CardContent>
                    {isOwner && (
                      <CardActions sx={{ justifyContent: "flex-end" }}>
                        <Tooltip title="Modifier">
                          <IconButton
                            size="small"
                            onClick={() => handleEditHouse(house)}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteHouse(house._id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </CardActions>
                    )}
                  </>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog de confirmation de suppression */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>Supprimer cette maison</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer cette maison ? Cette action ne
            peut pas être annulée.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Annuler</Button>
          <Button
            color="error"
            onClick={confirmDeleteHouse}
            disabled={removeHouseMutation.isPending}
            variant="contained"
          >
            {removeHouseMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              "Supprimer"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HouseManager;
